import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseClient';
import { logActivity } from './activityHelpers';

export const STREAK_TIERS = [
  { tier: 1, name: 'Bookworm Novice', icon: 'BookOpen', reward: 100 },
  { tier: 2, name: 'Curious Scholar', icon: 'Sparkles', reward: 150 },
  { tier: 3, name: 'Knowledge Seeker', icon: 'Compass', reward: 200 },
  { tier: 4, name: 'Academic Ace', icon: 'Award', reward: 300 },
  { tier: 5, name: 'Nesolearn Sage', icon: 'Crown', reward: 500 },
];

export const getTierInfo = (tierNumber) =>
  STREAK_TIERS.find((t) => t.tier === tierNumber) || STREAK_TIERS[0];

const getTodayDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const daysBetween = (dateStrA, dateStrB) => {
  const a = new Date(dateStrA + 'T00:00:00');
  const b = new Date(dateStrB + 'T00:00:00');
  return Math.round((b - a) / 86400000);
};

// Reads (and lazily initializes) the user's login-streak state, applies today's
// login-day check, persists any change, and returns the resulting state.
// NOTE: trusts the client's local date for the daily check, same accepted-limitation
// pattern as client-side grading/points elsewhere in this app.
export const getStreakState = async (userId) => {
  const ref = doc(db, 'learnerPoints', userId);
  const snap = await getDoc(ref);
  const today = getTodayDateString();

  let streak = snap.exists() ? snap.data()?.streak : null;

  if (!streak) {
    streak = {
      currentTier: 1,
      daysCompleted: 1,
      lastLoginDate: today,
      claimAvailable: false,
      lastClaimedAt: null,
    };
    await setDoc(
      ref,
      {
        userId,
        totalPoints: snap.exists() ? snap.data()?.totalPoints || 0 : 0,
        updatedAt: serverTimestamp(),
        streak,
      },
      { merge: true }
    );
    return streak;
  }

  // Already logged in today, or a reward is banked waiting on claim — no change.
  if (streak.lastLoginDate === today || streak.claimAvailable) {
    return streak;
  }

  const gap = daysBetween(streak.lastLoginDate, today);
  const nextDaysCompleted = gap === 1 ? Math.min(streak.daysCompleted + 1, 7) : 1;

  const updatedStreak = {
    ...streak,
    daysCompleted: nextDaysCompleted,
    lastLoginDate: today,
    claimAvailable: nextDaysCompleted >= 7,
  };

  await updateDoc(ref, { streak: updatedStreak, updatedAt: serverTimestamp() });
  return updatedStreak;
};

// Awards the current tier's NESOL reward, advances rank (capped at tier 5),
// and resets the weekly counter.
export const claimStreakReward = async (userId) => {
  const ref = doc(db, 'learnerPoints', userId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return { error: 'No streak found' };

  const streak = snap.data()?.streak;
  if (!streak?.claimAvailable) return { error: 'Nothing to claim yet' };

  const tierInfo = getTierInfo(streak.currentTier);
  const nextTier = Math.min(streak.currentTier + 1, STREAK_TIERS.length);

  const updatedStreak = {
    currentTier: nextTier,
    daysCompleted: 0,
    lastLoginDate: streak.lastLoginDate,
    claimAvailable: false,
    lastClaimedAt: serverTimestamp(),
  };

  await updateDoc(ref, {
    totalPoints: increment(tierInfo.reward),
    streak: updatedStreak,
    updatedAt: serverTimestamp(),
  });

  const nextTierInfo = getTierInfo(nextTier);
  await logActivity(userId, {
    type: 'level_up',
    label: `Leveled up to ${nextTierInfo.name}`,
    icon: nextTierInfo.icon,
    color: 'text-accent',
  });

  return { error: null, pointsAwarded: tierInfo.reward, newTier: nextTier };
};